<template>
  <div class="sales-warehouse-container" :class="{'dark': isDark}">
    <div class="chart-header">
      <h3>Sales by Store</h3>
    </div>
    
    <div v-if="loading" class="loading-indicator">
      <i class="material-icons spin">refresh</i>
      Loading data...
    </div>
    <div v-else-if="!warehouses.length" class="no-data">
      No data available for the selected period
    </div>
    <div v-else class="warehouse-content">
      <div class="warehouse-chart-wrapper">
        <EChartsHorizontalBarChart
          ref="warehouseRevenueChart"
          :categories="revenueChartCategories"
          :values="revenueChartValues"
          series-name="Revenue"
          aria-label="Sales by store revenue ranking"
          :is-dark="isDark"
          :value-formatter="formatCurrency"
        />
      </div>

      <div class="table-container">
        <table class="warehouse-table">
          <thead>
            <tr>
              <th class="store-col" @click="sortBy('warehouse_name')">
                Store
                <i v-if="sortColumn === 'warehouse_name'" class="material-icons sort-icon">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </i>
              </th>
              <th class="revenue-col" @click="sortBy('revenue')">
                Revenue
                <i v-if="sortColumn === 'revenue'" class="material-icons sort-icon">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </i>
              </th>
              <th class="orders-col" @click="sortBy('orders')">
                Orders
                <i v-if="sortColumn === 'orders'" class="material-icons sort-icon">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </i>
              </th>
              <th class="units-col" @click="sortBy('units')">
                Units Sold
                <i v-if="sortColumn === 'units'" class="material-icons sort-icon">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </i>
              </th>
              <th class="avg-col" @click="sortBy('average_order_value')">
                Avg. Order Value
                <i v-if="sortColumn === 'average_order_value'" class="material-icons sort-icon">
                  {{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
                </i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="warehouse in sortedWarehouses" :key="warehouse.warehouse_id">
              <td class="store-name">{{ warehouse.warehouse_name }}</td>
              <td class="amount">{{ formatCurrency(warehouse.revenue) }}</td>
              <td class="centered">{{ warehouse.orders }}</td>
              <td class="centered">{{ warehouse.units }}</td>
              <td class="amount">{{ formatCurrency(warehouse.average_order_value) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div class="card-footer">
      <router-link :to="reportRoute" class="view-report-link">
        View full report
        <i class="material-icons">arrow_forward</i>
      </router-link>
    </div>
  </div>
</template>

<script>
import SalesFetchApi from '../../utils/fetch/sales-fetch-api';
import EChartsHorizontalBarChart from './EChartsHorizontalBarChart.vue';
import { formatBusinessDate, getBusinessDateRangeForPreset } from '../../utils/business-date';

export default {
  name: 'SalesByWarehouseChart',
  components: {
    EChartsHorizontalBarChart
  },
  props: {
    isDark: {
      type: Boolean,
      default: false
    },
    initialDateRange: {
      type: String,
      default: 'last30days'
    },
    customDateRange: {
      type: Object,
      default: () => ({
        start: '',
        end: ''
      })
    },
    includePilotSales: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      warehouses: [],
      selectedRange: this.initialDateRange,
      startDate: '',
      endDate: '',
      sortColumn: 'revenue',
      sortDirection: 'desc',
      lastRequestKey: '',
      pendingRequestKey: '',
      fetchRequestId: 0
    };
  },
  computed: {
    reportRoute() {
      const query = {};
      if (this.startDate && this.endDate) {
        query.date_range_start = this.startDate;
        query.date_range_end = this.endDate;
      }
      if (this.includePilotSales) query.includePilotSales = "true";
      return {
        path: "/reports/sale-by-location",
        query
      };
    },
    revenueChartRows() {
      return [...this.warehouses]
        .sort((a, b) => Number(b.revenue || 0) - Number(a.revenue || 0))
        .slice(0, 6);
    },
    revenueChartCategories() {
      return this.revenueChartRows.map(warehouse => warehouse.warehouse_name || 'Unknown Store');
    },
    revenueChartValues() {
      return this.revenueChartRows.map(warehouse => Math.round(Number(warehouse.revenue || 0) * 100) / 100);
    },
    sortedWarehouses() {
      if (!this.warehouses.length || !this.sortColumn) {
        return this.warehouses;
      }
      
      return [...this.warehouses]
        .sort((a, b) => {
          const valueA = a[this.sortColumn];
          const valueB = b[this.sortColumn];
          
          if (valueA < valueB) return this.sortDirection === 'asc' ? -1 : 1;
          if (valueA > valueB) return this.sortDirection === 'asc' ? 1 : -1;
          return 0;
        })
        .slice(0, 10); // Limit to 10 items for display
    }
  },
  created() {
    this.updateDateRange();
  },
  watch: {
    initialDateRange(newValue) {
      if (newValue !== this.selectedRange) {
        this.selectedRange = newValue;
        this.updateDateRange();
      }
    },
    customDateRange: {
      handler(newValue) {
        if (this.selectedRange === 'custom' &&
            (newValue.start !== this.startDate || newValue.end !== this.endDate)) {
          this.startDate = newValue.start;
          this.endDate = newValue.end;
          this.fetchData();
        }
      },
      deep: true
    },
    includePilotSales() {
      this.fetchData();
    }
  },
  methods: {
    formatNumber(value) {
      return value ? value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
    },
    formatCurrency(value) {
      return `$${this.formatNumber(Number(value) || 0)}`;
    },
    updateDateRange() {
      if (this.selectedRange === 'custom') {
        if (this.customDateRange.start && this.customDateRange.end) {
          this.startDate = this.customDateRange.start;
          this.endDate = this.customDateRange.end;
        }
      } else {
        const range = getBusinessDateRangeForPreset(this.selectedRange);
        this.startDate = range.start;
        this.endDate = range.end;
      }
      
      this.fetchData();
    },
    setDateRange(range, customDates = null) {
      // This method can be called from parent component
      this.selectedRange = range;
      if (customDates && customDates.start && customDates.end) {
        this.startDate = customDates.start;
        this.endDate = customDates.end;
        this.fetchData();
      } else {
        this.updateDateRange();
      }
    },
    formatDate(date) {
      return formatBusinessDate(date);
    },
    async fetchData() {
      const requestKey = [this.startDate, this.endDate, this.includePilotSales].join('|');
      if (requestKey === this.pendingRequestKey) return;
      const requestId = ++this.fetchRequestId;
      this.pendingRequestKey = requestKey;
      const emitDataLoaded = (status, rows) => {
        if (typeof this.$emit === 'function') {
          this.$emit('data-loaded', { status, rows });
        }
      };

      try {
        this.loading = true;
        
        const response = await SalesFetchApi.getSalesByWarehouse({
          startDate: this.startDate,
          endDate: this.endDate,
          includePilotSales: this.includePilotSales
        });
        if (requestId !== this.fetchRequestId) return;
        
        if (response && response.result) {
          // Sort warehouses by revenue in descending order
          this.warehouses = [...response.result].sort((a, b) => b.revenue - a.revenue);
          emitDataLoaded('loaded', this.warehouses);
          this.lastRequestKey = requestKey;
        } else {
          this.warehouses = [];
          emitDataLoaded('loaded', []);
        }
      } catch (error) {
        if (requestId !== this.fetchRequestId) return;
        console.error('Error fetching warehouse sales data:', error);
        this.warehouses = [];
        emitDataLoaded('error', []);
      } finally {
        if (requestId === this.fetchRequestId) {
          this.pendingRequestKey = '';
          this.loading = false;
        }
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortDirection = 'asc';
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.sales-warehouse-container { background-color: $lightCardBg; border-radius: 8px; box-shadow: $cardShadow; padding: $cardPaddingY $cardPaddingX; margin-bottom: 0; border: 1px solid $lightBorderColor; transition: all 0.2s ease-in-out; }
.sales-warehouse-container:hover { box-shadow: $cardShadowHover; }

.card-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
}

.view-report-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: $primaryColor;
  text-decoration: none;
  font-weight: 600;
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  
  &:hover { background-color: rgba(13, 45, 44, 0.06); }
}

.dark.sales-warehouse-container { background-color: $darkCardBg; border-color: $darkBorderColor; color: $darkTextColor; }

.chart-header {
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h3 {
    margin: 0;
    font-size: 16px;
    color: $primaryColor;
    font-weight: 600;
  }
}

.dark .chart-header {
  h3 {
    color: $secondaryColorDark;
  }
}

.loading-indicator, 
.no-data {
  padding: 20px 0;
  text-align: center;
  color: $textColor;
  opacity: 0.7;
  font-size: 14px;
}

.dark .loading-indicator,
.dark .no-data {
  color: $darkTextColor;
  opacity: 0.7;
}

.loading-indicator .material-icons {
  margin-right: 8px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.warehouse-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.warehouse-chart-wrapper {
  height: 190px;
  min-height: 190px;
  padding-bottom: 10px;
  border-bottom: 1px solid $lightBorderColor;
}

.dark .warehouse-chart-wrapper {
  border-bottom-color: $darkBorderColor;
}

.table-container {
  overflow-x: auto;
  max-height: 220px;
  overflow-y: auto;
  margin-top: 0;
  
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  .dark &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
  }
}

.warehouse-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  
  th, td { padding: $tableCellPaddingY $tableCellPaddingX; text-align: left; border-bottom: 1px solid $lightBorderColor; }
  
  th { font-weight: 600; color: $textColor; font-size: $tableHeaderFontSize; cursor: pointer; position: sticky; top: 0; z-index: 1; backdrop-filter: none; background-color: $lightCardBg; border-bottom: 2px solid $lightBorderColor; }
  th:hover { background-color: rgba(0, 0, 0, 0.05); }
  th .sort-icon { font-size: 12px; vertical-align: middle; margin-left: 3px; display: inline-block; }
  
  tr:hover td { background-color: rgba(0, 0, 0, 0.01); }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  .warehouse-name {
    font-weight: 500;
  }
  
  .amount { color: $successColor; font-weight: 500; text-align: right; }
  
  .centered { text-align: right; }
}

.dark .warehouse-table {
  th {
    // Solid background for dark mode
    background-color: $darkCardBg; 
    color: $darkTextColor;
    border-color: $darkBorderColor;
    border-bottom: 2px solid $darkBorderColor;
    
    &:hover {
      background-color: rgba(255, 255, 255, 0.08);
    }
  }
  
  td {
    border-color: $darkBorderColor;
    color: $darkTextColor;
  }
  
  tr:hover td {
    background-color: rgba(255, 255, 255, 0.02);
  }
  
  .amount {
    color: $successColorDark;
  }
}
</style> 
